import { Layout as ANTDLayout } from "antd";
import Head from "next/head";
import Footer from "@/components/Footer";
import useDynamicModal from "@/base/utils/modal.hook";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { ClearQueryParams } from "@/base/utils/RouterUtils";
import WrapSupport from "@/components/main/WrapSupport";
import Styles from "@/base/styles/Header.module.scss";
import { Header } from "@/components/header";
import { ParallaxProvider } from "react-scroll-parallax";
import ReCaptchaParent from "@/base/utils/recaptcha";
import { lazyHydrate } from "next-lazy-hydration-on-scroll";

type Props = {
    children: React.ReactNode;
    rootClassName?: string;
    className?: string;
    beforeContent?: React.ReactNode;
};

const SupportWidget = lazyHydrate(() => import("@/components/support/SupportWidget"), {
    LoadingComponent: () => <div>Loading Support...</div>,
});

export default function Layout({ children, className, rootClassName, beforeContent }: Props) {
    const [hideSupportWidget, setHideSupportWidget] = useState(false);

    const router = useRouter();
    useEffect(() => {
        setHideSupportWidget(router.pathname.includes("/support"));
    }, [router.pathname]);
    const [SignOutModal, SignOutModalState, openSignOutModal] = useDynamicModal(() => import("@/base/components/modals/SignOut"));

    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (router.query.signout) {
            ClearQueryParams(["signout"], router);

            openSignOutModal();
        }
    }, [router.query.signout]);

    useEffect(() => {
        // make header opacity smooth change when scroll, also unbind scroll event when component unmounted
        if (headerRef.current) {
            const header = headerRef.current;
            const scrollHandler = () => {
                if (header) {
                    const scroll = Math.min(100, window.scrollY);

                    if (scroll > 50) {
                        header.classList.add(Styles.expanded);
                    } else {
                        header.classList.remove(Styles.expanded);
                    }
                }
            };

            window.addEventListener("scroll", scrollHandler);
            return () => {
                window.removeEventListener("scroll", scrollHandler);
            };
        }
    }, [headerRef.current]);

    return (
        <ANTDLayout className={`relative overflow-hidden`}>
            <Head>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" />
            </Head>
            {beforeContent && beforeContent}
            <ParallaxProvider>
                <Header className={`${Styles.header}`} ref={headerRef} />
                <div className={`min-h-screen`}>{children}</div>

                <Footer />
                <div className={`fixed right-0  md:right-5 bottom-5 z-[300]`}>
                    <ReCaptchaParent>
                        <SupportWidget stateHidden={[hideSupportWidget, setHideSupportWidget]} />
                    </ReCaptchaParent>
                </div>

                {SignOutModal && <SignOutModal state={SignOutModalState} />}
            </ParallaxProvider>
        </ANTDLayout>
    );
}
